import React from 'react';
import { Controller } from 'react-hook-form';
import { Alert, Button, Label, Select } from '@shared/components';
import { useLastCompletedServiceForm } from '../../../hooks/useLastCompletedServiceForm';
import { useLastServiceEditFocus } from '../../../hooks/useLastServiceEditFocus';
import type { LastCompletedServiceOptionsState } from '../../../hooks/useSmriqLastCompletedService';
import type {
    SmriqLastCompletedServiceOption,
    SmriqLastCompletedServiceSelection,
} from '../../../types/smriq.types';
import type { LastCompletedServiceFieldErrors } from '../../../utils/lastCompletedServiceFieldErrors';
import { lastCompletedServiceUpdateState } from '../../../utils/lastCompletedServiceUpdateState';
import { SmriqLastCompletedServiceSummary } from './SmriqLastCompletedServiceSummary';
import { ServiceDetailsFields } from './SmriqLastCompletedServicePanel/ServiceDetailsFields';

interface SmriqLastCompletedServicePanelProps {
    className?: string;
    options: readonly SmriqLastCompletedServiceOption[];
    optionsState: LastCompletedServiceOptionsState;
    nextService: SmriqLastCompletedServiceOption | null;
    /** The service the vendor matched on the last generate; null until one reports it. */
    autoMatchedService?: SmriqLastCompletedServiceOption | null;
    warnings: readonly string[];
    canGeneratePlan: boolean;
    selection: SmriqLastCompletedServiceSelection | null;
    selectionValid: boolean;
    selectionWithdrawn?: boolean;
    resetKey: number;
    planStartDate?: string;
    vehicleLastServiceDate?: string;
    vehicleLastServiceMileage?: number;
    serverErrors: Omit<LastCompletedServiceFieldErrors, 'step'>;
    hasPlan: boolean;
    isStale: boolean;
    isGenerating: boolean;
    sessionExpired: boolean;
    needsReconfirm: boolean;
    /** Duration/MOT edits in the plan controls that this panel's Update would not send. */
    planFieldsEdited?: boolean;
    /** False shows only the summary line and the Edit affordance; the form stays mounted. */
    expanded?: boolean;
    onEdit?: () => void;
    onCancel?: () => void;
    onChange: (selection: SmriqLastCompletedServiceSelection | null, valid: boolean) => void;
    onRetryPreview: () => void;
    onUpdate?: () => void;
}

const SELECT_ID = 'smriq_lcs_service_option';
const UPDATE_REASON_ID = 'smriq_lcs_update_reason';

export const SmriqLastCompletedServicePanel: React.FC<SmriqLastCompletedServicePanelProps> = ({
    className,
    options,
    optionsState,
    nextService,
    autoMatchedService = null,
    warnings,
    canGeneratePlan,
    selection,
    selectionValid,
    selectionWithdrawn = false,
    resetKey,
    planStartDate,
    vehicleLastServiceDate,
    vehicleLastServiceMileage,
    serverErrors,
    hasPlan,
    isStale,
    isGenerating,
    sessionExpired,
    needsReconfirm,
    planFieldsEdited = false,
    expanded = true,
    onEdit,
    onCancel,
    onChange,
    onRetryPreview,
    onUpdate,
}) => {
    const { control, maxDate, chooseOption, showDetails, autoMatchChosen, selectOptions } =
        useLastCompletedServiceForm({
            options,
            selection,
            resetKey,
            planStartDate,
            vehicleLastServiceDate,
            vehicleLastServiceMileage,
            serverErrors,
            onChange,
        });

    const { editButtonRef, handleEdit, handleCancel } = useLastServiceEditFocus({
        expanded,
        selectorId: SELECT_ID,
        onEdit,
        onCancel,
    });

    const controlsDisabled = isGenerating || sessionExpired || needsReconfirm;
    const matchedService = autoMatchChosen ? autoMatchedService : null;
    const serviceHint = [
        nextService ? `Next service due: ${nextService.label}` : null,
        matchedService ? `Auto matched service: ${matchedService.label}` : null,
    ]
        .filter(Boolean)
        .join(' · ');
    const optionsLoading = optionsState === 'loading';
    const { canUpdate, reason: updateReason } = lastCompletedServiceUpdateState({
        hasUpdateHandler: !!onUpdate,
        isStale,
        selectionValid,
        canGeneratePlan,
        isGenerating,
        sessionExpired,
        needsReconfirm,
        planFieldsEdited,
    });

    return (
        <div className={`space-y-3 ${className ?? ''}`}>
            {warnings.length > 0 && (
                <Alert variant="info" role="status">
                    <ul className="space-y-1">
                        {warnings.map((warning) => (
                            <li key={warning}>{warning}</li>
                        ))}
                    </ul>
                </Alert>
            )}

            <section
                className="bg-mp-surface rounded-xl space-y-4"
                aria-label="Last Completed Service"
                aria-busy={isGenerating}
            >
                {!expanded && (
                    <SmriqLastCompletedServiceSummary
                        selection={selection}
                        onEdit={handleEdit}
                        editButtonRef={editButtonRef}
                    />
                )}

                {optionsState === 'empty' && (
                    <p className="text-sm text-mp-muted">
                        No service history options are available for this vehicle
                    </p>
                )}

                {selectionWithdrawn && (
                    <Alert variant="warning" role="status">
                        The selected service is no longer available for this mileage. Please choose
                        again.
                    </Alert>
                )}

                {!canGeneratePlan && (
                    <p role="alert" className="text-sm text-mp-error">
                        A plan cannot be generated for this vehicle yet.
                    </p>
                )}

                {expanded && (
                    // The buttons are pinned to columns 4/5 to stay right-aligned, as in the plan controls.
                    <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_1fr_auto_auto] gap-4 items-start">
                        <div>
                            <Label htmlFor={SELECT_ID}>Service completed</Label>
                            <Controller
                                name="service_option_id"
                                control={control}
                                render={({ field, fieldState: { error } }) => (
                                    <Select
                                        id={SELECT_ID}
                                        ariaLabel="Service completed"
                                        options={selectOptions}
                                        value={field.value}
                                        disabled={controlsDisabled}
                                        isLoading={optionsLoading}
                                        error={!!error}
                                        hint={error?.message}
                                        onChange={chooseOption}
                                        onBlur={field.onBlur}
                                    />
                                )}
                            />
                        </div>
                        <ServiceDetailsFields
                            control={control}
                            maxDate={maxDate}
                            disabled={controlsDisabled || optionsLoading || !showDetails}
                        />
                        {hasPlan && (
                            <div className="flex flex-col self-stretch sm:col-start-4">
                                <label className="mb-1.5 text-sm font-medium">&nbsp;</label>
                                <Button
                                    type="button"
                                    size="sm"
                                    className="whitespace-nowrap"
                                    disabled={!canUpdate}
                                    ariaDescribedBy={updateReason ? UPDATE_REASON_ID : undefined}
                                    onClick={onUpdate}
                                >
                                    {isGenerating ? 'Generating...' : 'Update plan'}
                                </Button>
                            </div>
                        )}
                        {hasPlan && handleCancel && (
                            <div className="flex flex-col self-stretch sm:col-start-5">
                                <label className="mb-1.5 text-sm font-medium">&nbsp;</label>
                                <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    className="whitespace-nowrap"
                                    disabled={isGenerating}
                                    onClick={handleCancel}
                                >
                                    Cancel
                                </Button>
                            </div>
                        )}
                    </div>
                )}

                {optionsState === 'failed' && (
                    <div className="flex items-center gap-3 text-sm text-mp-muted">
                        <span>The service options could not be loaded.</span>
                        <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            disabled={controlsDisabled}
                            onClick={onRetryPreview}
                        >
                            Try again
                        </Button>
                    </div>
                )}

                {expanded && serviceHint && <p className="text-sm text-mp-muted">{serviceHint}</p>}

                {expanded && hasPlan && updateReason && (
                    <p id={UPDATE_REASON_ID} className="text-sm text-mp-muted">
                        {updateReason}
                    </p>
                )}
            </section>
        </div>
    );
};
